var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import EthLibAdapter from '../EthLibAdapter';
import Web3ContractAdapter from './Web3ContractAdapter';
import { formatCallTx, normalizeGasLimit } from '../../utils/transactions';
export function toTxResult(promiEvent, sendOptions) {
    return new Promise((resolve, reject) => promiEvent
        .once('transactionHash', (hash) => resolve({ sendOptions, promiEvent, hash }))
        .catch(reject));
}
class Web3Adapter extends EthLibAdapter {
    constructor({ web3 }) {
        super();
        if (!web3) {
            throw new Error('web3 property missing from options');
        }
        this.web3 = web3;
    }
    getProvider() {
        return this.web3.currentProvider;
    }
    providerSend(method, params) {
        // TO-DO: Use semver comparison
        return this.web3.version.split('.')[0] > 1
            ? this.web3.currentProvider.send(method, params)
            : new Promise((resolve, reject) => this.web3.currentProvider.send({
                jsonrpc: '2.0',
                id: new Date().getTime(),
                method,
                params
            }, (err, result) => {
                if (err)
                    return reject(err);
                if (result.error)
                    return reject(result.error);
                return resolve(result.result);
            }));
    }
    signMessage(message, ownerAccount) {
        return this.web3.eth.sign(message, ownerAccount);
    }
    getNetworkId() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.web3.eth.net.getId();
        });
    }
    getAccount() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.web3.eth.defaultAccount || (yield this.web3.eth.getAccounts())[0];
        });
    }
    keccak256(data) {
        return this.web3.utils.keccak256(data);
    }
    abiEncode(types, values) {
        return this.web3.eth.abi.encodeParameters(types, values);
    }
    abiDecode(types, data) {
        return this.web3.eth.abi.decodeParameters(types, data);
    }
    calcCreate2Address(deployer, salt, initCode) {
        return this.web3.utils.toChecksumAddress(this.web3.utils
            .soliditySha3('0xff', { t: 'address', v: deployer }, { t: 'bytes32', v: salt }, this.keccak256(initCode))
            .slice(-40));
    }
    getCode(address) {
        return this.web3.eth.getCode(address);
    }
    getBlock(blockHashOrBlockNumber) {
        return this.web3.eth.getBlock(blockHashOrBlockNumber);
    }
    getContract(abi, address) {
        const contract = new this.web3.eth.Contract(abi, address);
        return new Web3ContractAdapter(contract);
    }
    getCallRevertData(tx, block) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // this block handles old Geth/Ganache --noVMErrorsOnRPCResponse
                // use a low level eth_call instead of web3.eth.call so
                // full error data from eth node is available if provider is Web3 1.x
                return yield this.providerSend('eth_call', [formatCallTx(tx), block]);
            }
            catch (e) {
                let errData = e.data;
                if (!errData && e.message.startsWith('Node error: ')) {
                    // parse out error data from eth node if provider is Web3 2.x
                    errData = JSON.parse(e.message.slice(12)).data;
                }
                if (typeof errData === 'string') {
                    if (errData.startsWith('Reverted 0x'))
                        // handle OpenEthereum revert data format
                        return errData.slice(9);
                    if (errData.startsWith('0x'))
                        // handle new Geth format
                        return errData;
                }
                // handle Ganache revert data format
                const txHash = Object.getOwnPropertyNames(errData).filter((k) => k.startsWith('0x'))[0];
                return errData[txHash].return;
            }
        });
    }
    ethSendTransaction(tx) {
        return toTxResult(this.web3.eth.sendTransaction(normalizeGasLimit(tx)), tx);
    }
    toSafeRelayTxResult(txHash, tx) {
        tx['transactionHash'] = tx['txHash'];
        delete tx['txHash'];
        return new Promise((resolve, reject) => resolve({
            promiEvent: new Promise((resolve, reject) => resolve(tx)),
            hash: txHash
        }));
    }
}
export default Web3Adapter;
//# sourceMappingURL=index.js.map